import { useEffect, useRef, useState } from 'react';
import { authoredDates, availableDates, calendarDay, monthCells } from '../home.ts';
import { RewardDrawing } from './RewardDrawing.tsx';
import type { RewardKind } from '../home.ts';
import { subjectLabels } from '../types.ts';
import type { Progress } from '../types.ts';

const fullDate = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${date}T12:00:00`));
const months = [...new Set(authoredDates.map((date) => date.slice(0, 7)))];
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function CalendarSeal({ kind }: { kind: RewardKind }) {
  return (
    <svg viewBox="0 0 120 120" className="calendar-seal" aria-hidden="true">
      <rect
        x="3"
        y="3"
        width="114"
        height="114"
        rx="14"
        fill={kind === 'sun' ? '#fbefce' : '#e9efe8'}
        stroke={kind === 'sun' ? '#b6924d' : '#829789'}
        strokeWidth="4"
        strokeDasharray="8 6"
      />
      <g transform="translate(0 19)" color="#486549">
        <RewardDrawing kind={kind} />
      </g>
    </svg>
  );
}

export function HomeCalendar({
  date,
  today,
  progress,
  mapCompletions,
  onDate,
}: {
  date: string;
  today: string;
  progress: Progress;
  mapCompletions: Record<string, boolean>;
  onDate: (date: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(date.slice(0, 7));
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const dates = availableDates(today);
  const index = dates.indexOf(date);
  const previous = dates[index - 1];
  const next = dates[index + 1];
  const monthIndex = months.indexOf(month);
  const selected = new Date(`${date}T12:00:00`);
  const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(`${month}-01T12:00:00`),
  );
  useEffect(() => {
    if (!open) return;
    function outside(event: PointerEvent) {
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    }
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  function choose(nextDate: string) {
    if (!dates.includes(nextDate)) return;
    setOpen(false);
    onDate(nextDate);
  }
  return (
    <div className="home-dates" ref={wrapper}>
      <div className="home-date-strip">
        <button
          className="open-calendar"
          ref={trigger}
          aria-expanded={open}
          aria-controls="practice-calendar"
          aria-label={`Select date, ${fullDate(date)}`}
          onClick={() => {
            setMonth(date.slice(0, 7));
            setOpen(!open);
          }}
        >
          <span className="calendar-leaf" aria-hidden="true">
            <span>{new Intl.DateTimeFormat('en-US', { month: 'short' }).format(selected)}</span>
            <strong>{selected.getDate()}</strong>
          </span>
          <span className="calendar-caption">
            <span className="calendar-current-date">{fullDate(date)}</span>
            <span className="calendar-prompt">
              Select date <span aria-hidden="true">⌄</span>
            </span>
          </span>
        </button>
        <div className="home-date-steps" role="group" aria-label="Change practice date">
          <button disabled={!previous} onClick={() => previous && choose(previous)}>
            <span aria-hidden="true">←</span> Previous day
          </button>
          <button disabled={!next} onClick={() => next && choose(next)}>
            Next day <span aria-hidden="true">→</span>
          </button>
          {date !== today && dates.includes(today) && (
            <button className="return-today" onClick={() => choose(today)}>
              Go to today
            </button>
          )}
        </div>
      </div>
      {open && (
        <section
          className="practice-calendar"
          id="practice-calendar"
          aria-label="Practice calendar"
        >
          <div className="calendar-toolbar">
            <button
              aria-label="Previous month"
              disabled={monthIndex <= 0}
              onClick={() => setMonth(months[monthIndex - 1])}
            >
              ←
            </button>
            <h2 aria-live="polite">{monthLabel}</h2>
            <button
              aria-label="Next month"
              disabled={monthIndex < 0 || monthIndex === months.length - 1}
              onClick={() => setMonth(months[monthIndex + 1])}
            >
              →
            </button>
            <button
              className="close-calendar"
              aria-label="Close calendar"
              onClick={() => {
                setOpen(false);
                trigger.current?.focus();
              }}
            >
              ×
            </button>
          </div>
          <div className="calendar-weekdays" aria-hidden="true">
            {weekdays.map((day) => (
              <span key={day}>{day.slice(0, 2)}</span>
            ))}
          </div>
          <div className="calendar-days">
            {monthCells(month).map((day, i) => {
              if (!day) return <span className="calendar-blank" key={`blank-${i}`} />;
              const state = calendarDay(progress, day, today, !!mapCompletions[day]);
              const enabled = state.availability === 'available';
              const status = !enabled
                ? state.availability === 'scheduled'
                  ? `Unlocks ${fullDate(day)}`
                  : 'No practice scheduled'
                : state.selected.length
                  ? `${state.completed.length} of ${state.selected.length} selected subjects complete${state.reward ? `, ${state.reward} stamp` : ''}`
                  : 'Available, choose subjects';
              const detail = state.selected
                .map(
                  (subject) =>
                    `${subjectLabels[subject]} ${state.completed.includes(subject) ? 'complete' : 'unfinished'}`,
                )
                .join(', ');
              return (
                <button
                  key={day}
                  className={`calendar-day day-${state.availability}${day === date ? ' day-selected' : ''}${state.done ? ' day-complete' : ''}`}
                  disabled={!enabled}
                  aria-current={day === today ? 'date' : undefined}
                  aria-pressed={enabled ? day === date : undefined}
                  aria-label={`${fullDate(day)}. ${status}${detail ? '. ' + detail : ''}`}
                  title={status}
                  onClick={() => choose(day)}
                >
                  <span className="day-number">
                    {Number(day.slice(-2))}
                    {state.reward && <CalendarSeal kind={state.reward} />}
                  </span>
                  {state.selected.length ? (
                    <span className="day-checks" aria-hidden="true">
                      {state.selected.map((subject) => (
                        <span
                          key={subject}
                          className={`day-check${state.completed.includes(subject) ? ' checked' : ''}`}
                          title={subjectLabels[subject]}
                        >
                          {state.completed.includes(subject) ? '✓' : ''}
                        </span>
                      ))}
                    </span>
                  ) : enabled ? (
                    <span className="day-ready" aria-hidden="true">
                      Choose
                    </span>
                  ) : state.availability === 'scheduled' ? (
                    <svg className="day-lock" viewBox="0 0 16 18" aria-hidden="true">
                      <path
                        d="M4 7V5a4 4 0 0 1 8 0v2M2 7h12v9H2Z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.2"
                      />
                    </svg>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="calendar-key">
            <span>
              <i className="day-check" /> Selected
            </span>
            <span>
              <i className="day-check checked">✓</i> Finished
            </span>
            <span>
              <CalendarSeal kind="moon" /> Selection complete
            </span>
            <span>
              <CalendarSeal kind="sun" /> All five complete
            </span>
          </div>
          <p className="calendar-note">Choose any available day. Future dates unlock daily.</p>
        </section>
      )}
    </div>
  );
}
